import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileAlt, faCheckCircle, faHourglass, faTruck,
    faWeightHanging, faPrint, faBan,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const StatCard = ({ label, value, sub, icon, color, bg, border }) => (
    <div
        style={{
            flex: '1 1 180px', minWidth: 170, maxWidth: 260,
            backgroundColor: '#fff',
            border: `1px solid ${border}`,
            borderRadius: 14,
            padding: '16px 18px',
            display: 'flex', flexDirection: 'column', gap: 8,
            transition: 'box-shadow 0.15s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = `0 4px 16px ${border}bb`)}
        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
    >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {label}
            </span>
            <div style={{
                width: 32, height: 32, borderRadius: 8,
                backgroundColor: bg, display: 'flex', alignItems: 'center',
                justifyContent: 'center', color, fontSize: 13, flexShrink: 0, marginLeft: 8,
            }}>
                <FontAwesomeIcon icon={icon} />
            </div>
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#111827', lineHeight: 1 }}>{value ?? '—'}</div>
        {sub && <div style={{ fontSize: 11, color: '#9ca3af' }}>{sub}</div>}
    </div>
);

const SectionBlock = ({ title, accent, cards }) => (
    <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <div style={{ width: 3, height: 18, borderRadius: 2, backgroundColor: accent }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{title}</span>
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {cards.map((c) => <StatCard key={c.label} {...c} />)}
        </div>
    </div>
);

const fmt = (n) => (n != null ? Number(n).toLocaleString() : '—');
const fmtWt = (n) => (n != null ? `${parseFloat(n).toFixed(2)} T` : '—');

const ChallanSummaryCards = ({ summary }) => {
    const { t: translate } = useTranslation();
    if (!summary) return null;

    const t = summary.totals ?? {};
    const tp = summary.top_product;

    const overviewCards = [
        {
            label: translate('challanAnalytics.cards.totalChallans'),
            value: fmt(t.total_challans),
            sub: `${fmt(t.unique_customers)} ${translate('challanAnalytics.labels.uniqueCustomersSuffix')}`,
            icon: faFileAlt,
            color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc',
        },
        {
            label: translate('challanAnalytics.cards.totalWeight'),
            value: fmtWt(t.total_weight_tons),
            sub: `${fmt(t.total_weight_kg)} ${translate('common.units.kg')}`,
            icon: faWeightHanging,
            color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe',
        },
        {
            label: translate('challanAnalytics.cards.printed'),
            value: fmt(t.printed),
            sub: t.unprinted != null ? `${fmt(t.unprinted)} ${translate('challanAnalytics.labels.notPrintedSuffix')}` : undefined,
            icon: faPrint,
            color: '#d97706', bg: '#fffbeb', border: '#fde68a',
        },
    ];

    const statusCards = [
        {
            label: translate('common.analyticsColumns.delivered'),
            value: fmt(t.delivered),
            sub: translate('challanAnalytics.cards.subs.successfullyDelivered'),
            icon: faCheckCircle,
            color: '#059669', bg: '#ecfdf5', border: '#a7f3d0',
        },
        {
            label: translate('common.analyticsColumns.inTransit'),
            value: fmt(t.in_transit),
            sub: translate('challanAnalytics.cards.subs.currentlyMoving'),
            icon: faTruck,
            color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe',
        },
        {
            label: translate('common.analyticsColumns.loaded'),
            value: fmt(t.loaded),
            sub: translate('challanAnalytics.cards.subs.loadedNotDispatched'),
            icon: faFileAlt,
            color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc',
        },
        ...(t.cancelled != null ? [{
            label: translate('common.analyticsColumns.cancelled'),
            value: fmt(t.cancelled),
            sub: translate('challanAnalytics.cards.subs.cancelledChallans'),
            icon: faBan,
            color: '#ef4444', bg: '#fef2f2', border: '#fecaca',
        }] : []),
        ...(t.pending != null ? [{
            label: translate('common.analyticsColumns.pending'),
            value: fmt(t.pending),
            sub: translate('challanAnalytics.cards.subs.awaitingAction'),
            icon: faHourglass,
            color: '#d97706', bg: '#fffbeb', border: '#fde68a',
        }] : []),
    ];

    const hasStatusData = (t.delivered ?? t.in_transit ?? t.loaded) != null;

    return (
        <div>
            <SectionBlock title={translate('challanAnalytics.sections.overview')} accent="#189AB4" cards={overviewCards} />
            {hasStatusData && <SectionBlock title={translate('challanAnalytics.sections.byStatus')} accent="#7c3aed" cards={statusCards} />}
            {tp && (
                <div style={{ marginBottom: 28 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                        <div style={{ width: 3, height: 18, borderRadius: 2, backgroundColor: '#059669' }} />
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{translate('challanAnalytics.sections.topProduct')}</span>
                    </div>
                    <div style={{
                        backgroundColor: '#fff', border: '1px solid #a7f3d0', borderRadius: 14,
                        padding: '16px 20px', display: 'flex', flexWrap: 'wrap', gap: 24,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                    }}>
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{translate('challanAnalytics.labels.product')}</div>
                            <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{tp.product_name ?? '—'}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{translate('challanAnalytics.labels.challans')}</div>
                            <div style={{ fontSize: 15, fontWeight: 800, color: '#0891b2' }}>{fmt(tp.challan_count)}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{translate('challanAnalytics.labels.qty')}</div>
                            <div style={{ fontSize: 15, fontWeight: 800, color: '#374151' }}>{fmt(tp.total_quantity)}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{translate('challanAnalytics.labels.weight')}</div>
                            <div style={{ fontSize: 15, fontWeight: 800, color: '#7c3aed' }}>{fmtWt(tp.total_weight_tons)}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChallanSummaryCards;
